import { api } from '../../lib/api';
import type {
  AuditEntry,
  CronUserEventsFilters,
  CronUserEventsResponse,
  SubscriptionTier,
  UserDetail,
  UserDevice,
  UserListResponse,
  UserRole,
} from './types';

export interface UserListFilters {
  search?: string;
  role?: UserRole;
  subscription?: 'FREE' | 'PREMIUM' | 'TRIAL' | 'CANCELLED';
  status?: 'active' | 'suspended' | 'deleted';
  sort?: 'createdAt' | 'lastSeenAt' | 'email';
  order?: 'asc' | 'desc';
  cursor?: string | null;
  limit?: number;
  // App-Kontext aus dem globalen Picker (weggelassen = "Alle Apps").
  app?: string;
}

function buildQuery(filters: UserListFilters): string {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.role) params.set('role', filters.role);
  if (filters.subscription) params.set('subscription', filters.subscription);
  if (filters.status) params.set('status', filters.status);
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.order) params.set('order', filters.order);
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.app) params.set('app', filters.app);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Rolle eines Nutzers setzen (CUR-995 macht das im Panel erstmals bedienbar —
 * der Endpoint existierte schon, wurde aber von keiner Oberflaeche gerufen).
 * Step-up-Pflichtig, das Backend protokolliert die Aenderung im Audit-Log.
 */
export function changeUserRole(
  id: string,
  role: UserRole,
  reason: string,
  stepUpToken: string,
): Promise<{ success: boolean }> {
  return api.patch<{ success: boolean }>(`/admin/users/${id}/role`, { role, reason }, { stepUpToken });
}

export function fetchUsers(filters: UserListFilters): Promise<UserListResponse> {
  return api.get<UserListResponse>(`/admin/users${buildQuery(filters)}`);
}

export function fetchUserDetail(id: string, app: string): Promise<UserDetail> {
  // `app` scopt das Lernprofil + Aktivitäts-Counts (Apps-&-Abos bleibt cross-app).
  return api.get<UserDetail>(`/admin/users/${id}?app=${encodeURIComponent(app)}`);
}

export function fetchUserAudit(id: string, limit = 50): Promise<AuditEntry[]> {
  return api.get<AuditEntry[]>(`/admin/users/${id}/audit?limit=${limit}`);
}

export function fetchUserDevices(id: string, app: string): Promise<UserDevice[]> {
  return api.get<UserDevice[]>(`/admin/users/${id}/devices?app=${encodeURIComponent(app)}`);
}

export function fetchUserCronEvents(
  id: string,
  app: string,
  filters: CronUserEventsFilters = {},
): Promise<CronUserEventsResponse> {
  const params = new URLSearchParams();
  params.set('app', app);
  if (filters.jobName) params.set('jobName', filters.jobName);
  if (filters.action) params.set('action', filters.action);
  if (filters.since) params.set('since', filters.since);
  if (filters.until) params.set('until', filters.until);
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.limit) params.set('limit', String(filters.limit));
  return api.get<CronUserEventsResponse>(`/admin/users/${id}/cron-events?${params.toString()}`);
}

export function suspendUser(id: string, reason: string, stepUpToken: string): Promise<void> {
  return api.post(`/admin/users/${id}/suspend`, { reason }, { stepUpToken });
}

export function unsuspendUser(id: string, stepUpToken: string, reason?: string): Promise<void> {
  return api.post(`/admin/users/${id}/unsuspend`, reason ? { reason } : {}, { stepUpToken });
}

export function softDeleteUser(id: string, stepUpToken: string, reason?: string): Promise<void> {
  return api.delete(`/admin/users/${id}`, {
    stepUpToken,
    body: reason ? JSON.stringify({ reason }) : undefined,
    headers: reason ? { 'Content-Type': 'application/json' } : {},
  });
}

export function overrideSubscription(
  id: string,
  app: string,
  payload: { tier: SubscriptionTier; expiresAt: string | null; reason: string },
  stepUpToken: string,
): Promise<void> {
  return api.post(
    `/admin/users/${id}/subscription?app=${encodeURIComponent(app)}`,
    payload,
    { stepUpToken },
  );
}

export function triggerPasswordReset(id: string): Promise<void> {
  return api.post(`/admin/users/${id}/reset-password`);
}

export function setUserAddon(
  id: string,
  app: string,
  payload: { addon: string; action: 'grant' | 'revoke'; reason: string },
  stepUpToken: string,
): Promise<{ success: boolean; data: { addons: string[] } }> {
  return api.post<{ success: boolean; data: { addons: string[] } }>(
    `/admin/users/${id}/addons?app=${encodeURIComponent(app)}`,
    payload,
    { stepUpToken },
  );
}

// ─── Public-Profile (Article-CMS Author/Reviewer) ─────────────

export interface PublicProfileInput {
  publicSlug?: string | null;
  publicTitle?: string | null;
  publicBio?: string | null;
  publicAvatarUrl?: string | null;
  credentials?: string[];
  socialLinks?: Record<string, string> | null;
}

export interface PublicProfileResponse {
  id: string;
  publicSlug: string | null;
  publicTitle: string | null;
  publicBio: string | null;
  publicAvatarUrl: string | null;
  credentials: string[];
  socialLinks: Record<string, string> | null;
}

export function updateUserPublicProfile(
  id: string,
  input: PublicProfileInput,
): Promise<{ success: boolean; data: PublicProfileResponse }> {
  return api.patch<{ success: boolean; data: PublicProfileResponse }>(
    `/admin/users/${id}/public-profile`,
    input,
  );
}
